import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { axiosInstance } from "../../../utils/client";
import { setPaginationOption } from "../filter/filterSlice";

type FavoriteItem = {
  id: number;
  userId: number;
  businessLocationId: number;
  productId: number;
  favorite: boolean;
};

type FavoriteState = {
  favorites: FavoriteItem[];
  isLoading: boolean;
  error: string | false;
  totalFavorites: number;
  pageSize: number;
  pageCount: number;
  isFavLoggedin: boolean;
  selectedFavorite: any;
  favListLoggedin: boolean;
};

const initialState: FavoriteState = {
  favorites: [],
  isLoading: false,
  error: false,
  totalFavorites: 0,
  pageSize: 10,
  pageCount: 1,
  isFavLoggedin: false,
  selectedFavorite: null,
  favListLoggedin: false,
};

const favoriteSlice = createSlice({
  name: "favorite",
  initialState,
  reducers: {
    addFavorite: (state, action) => {
      state.favorites.push(action.payload);
      state.selectedFavorite = null;
      state.isFavLoggedin = true;
    },
    setFavorites: (state, action) => {
      state.favorites = action.payload.favorites;
      state.totalFavorites = action.payload.totalFavorites;
      state.pageSize = action.payload.pageSize;
      state.pageCount = action.payload.pageCount;
    },
    setFavoriteLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setFavoriteError: (state, action) => {
      state.error = action.payload;
    },
    removeFavorite: (state, action) => {
      state.favorites = state.favorites.filter(
        (favorite) => favorite.id !== action.payload
      );
      state.totalFavorites -= 1;
    },
    setIsLoggedin: (state, action) => {
      state.isFavLoggedin = action.payload;
    },
    setSelectedFavorite: (state, action) => {
      state.selectedFavorite = action.payload;
      // console.log("🚀 ~ payload:", action.payload);
    },
    setFavLoggedIn: (state, action) => {
      state.favListLoggedin = action.payload;
    }
  },
});

export const {
  addFavorite,
  setFavorites,
  setFavoriteLoading,
  setFavoriteError,
  removeFavorite,
  setIsLoggedin,
  setSelectedFavorite,
  setFavLoggedIn
} = favoriteSlice.actions;
export default favoriteSlice.reducer;

// ----------------------------------------------------------------------//

export function saveFavorite(data: {
  // businessLocationId: number;
  productId: number;
}) {
  console.log("🚀 ~ productId:", data.productId);
  return async (dispatch: any, getState: () => any) => {
    const { auth } = getState().auth;
    const { selectedBusinessLocation } = getState().businessLocation;

    try {
      if (!auth) {
        dispatch(setIsLoggedin(false));
        dispatch(setSelectedFavorite(data));
        return;
      }
      const formattedData = {
        userId: auth.userId,
        businessLocationId: selectedBusinessLocation.id,
        branchProductId: data.productId,
      };

      const response = await axiosInstance.post("/token/favorite/change", formattedData);

      if (response?.data?.success) {
        toast.success(response.data.message);
        dispatch(setSelectedFavorite(null));
        dispatch(getFavorites());
      }

      return response.data;
    } catch (error) {
      console.error("Error saving favorite:", error);
      return { success: false, message: "Failed to save favorite." };
    }
  };
}

export function getFavorites() {
  return async (dispatch: any, getState: () => any) => {
    const paginationOption = getState().filter.paginationOption;

    try {
      dispatch(setFavoriteLoading(true));

      const userId = getState().auth.auth?.userId;
      const { selectedBusinessLocation } = getState().businessLocation;
      const businessLocationId = selectedBusinessLocation.id;
      if (!userId) {
        throw new Error("User ID is missing. Please log in.");
      }
      if (!businessLocationId) {
        throw new Error("Business Location ID is missing.");
      }

      let req = `userId=${userId}&businessLocationId=${businessLocationId}`;

      // Process filters
      // if (Array.isArray(filters)) {
      //   filters.forEach((filter: any) => {
      //     if (filter?.title === "Business Location") {
      //       filter.filters.forEach((item: any) => {
      //         if (item?.selected) {
      //           req += `&businessLocationId=${item.value}`;
      //         }
      //       });
      //     }
      //     if (filter?.title === "Product") {
      //       filter.filters.forEach((item: any) => {
      //         if (item?.selected) {
      //           req += `&productId=${item.value}`;
      //         }
      //       });
      //     }
      //     if (filter?.title === "Favorite Status") {
      //       filter.filters.forEach((item: any) => {
      //         if (item?.selected) {
      //           req += `&isFavorite=${item.value}`;
      //         }
      //       });
      //     }
      //   });
      // }

      // Add pagination options if present
      if (paginationOption?.page) {
        req += `&pageCount=${paginationOption.page}`;
      }
      if (paginationOption?.pageSize) {
        req += `&pageSize=${paginationOption.pageSize}`;
      }

      const response = await axiosInstance.get(`/token/favorite/filter?${req}`);
      // console.log("Favorites response:", response.data);

      if (response.status === 200) {
        const { records, pageNumber, pageSize, totalPages, totalRecords } =
          response.data;

        dispatch(
          setFavorites({
            favorites: records,
            totalFavorites: totalRecords,
          })
        );

        dispatch(
          setPaginationOption({
            page: pageNumber,
            pageSize,
            totalPages,
            totalRecords,
          })
        );
        return response.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      console.error("Error fetching favorites:", error);
      toast.error(error.message || "Failed to fetch favorite items.");
      dispatch(
        setFavoriteError(error.message || "Failed to fetch favorite items.")
      );
    } finally {
      dispatch(setFavoriteLoading(false));
    }
  };
}

export function favoriteRemove(id: number) {
  return async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(`/Favorite/${id}`);

      if (response?.data?.success) {
        toast.success(response.data.message);
        dispatch(removeFavorite(id));
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      console.error("Error removing favorite:", error);
      toast.error("Failed to remove favorite item.");
    }
  };
}
